import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { iconFor } from "../lib/icons";
import { mediaVariant, mediaSrcSet } from "../lib/catalogueApi";

// T59: one swipeable row on every width (replaces the T56 phone grid). Real product photos on a tinted tile; the icon is
// the fallback only when no real photo exists. `phone` / `preview` drive the admin live previews.
const TILE = "rs-cattile group relative flex shrink-0 grow snap-start flex-col rounded-2xl bg-[#E8F0FA] p-2 text-[var(--brand-primary)] outline-none transition-[box-shadow,transform,background-color] duration-200 hover:bg-[#DCE8F7] hover:shadow-md focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] focus-visible:ring-offset-2 active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100";
const W = { row: "h-[196px] sm:h-[212px]", tile: "w-[max(36vw,136px)] max-w-[168px] sm:w-[160px] sm:max-w-[200px] lg:w-[156px]" };
const WP = { row: "h-[196px]", tile: "w-[140px] max-w-[140px]" };
const smooth = () => (window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth");

function Photo({ t }) {
  const Icon = iconFor(t.icon);
  if (!t.photo) return <span className="flex h-full w-full items-center justify-center text-[var(--brand-link)]" data-testid={`tile-icon-fallback-${t.category_id}`}><Icon size={44} strokeWidth={1.4} aria-hidden="true" /></span>;
  return (
    <span className="flex h-full w-full items-center justify-center overflow-hidden p-1">
      <img src={mediaVariant(t.photo.media_id, 320, "webp")} srcSet={mediaSrcSet(t.photo.media_id, "webp", [160, 320, 640])} sizes="160px" alt="" loading="lazy" decoding="async"
        width="160" height="120" className="h-full w-full object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100" data-testid={`tile-photo-${t.category_id}`} />
    </span>
  );
}

function Tile({ t, i, preview, w, onKey }) {
  const inner = (<>
    <span className="block h-[118px] sm:h-[132px]"><Photo t={t} /></span>
    <span className="relative mt-1.5 flex min-h-[40px] flex-1 items-end px-1 pb-1 pr-4">
      <span lang="en" className="min-w-0 text-[12.5px] max-[379px]:text-[12px] font-bold leading-[1.25] line-clamp-2 [hyphens:auto] sm:text-[13.5px]">{t.label}</span>
      <ChevronRight size={16} strokeWidth={2.25} aria-hidden="true" className="absolute bottom-1 right-0 shrink-0 text-[var(--brand-primary)] transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none" />
    </span>
  </>);
  const props = { className: `${TILE} ${w.tile}`, "data-i": i, onKeyDown: onKey };
  return preview ? <div {...props} data-testid={`preview-tile-${t.category_id}`}>{inner}</div>
    : <Link {...props} to={`/c/${t.slug}`} data-testid={`home-cat-${t.slug}`}>{inner}</Link>;
}

// T64: mouse drag-to-scroll with gentle momentum, then the row's snap settles it. Touch keeps native swipe + momentum + snap.
// A drag over 6px swallows the tile click; a plain click still navigates. Reduced motion: no momentum.
function useDragScroll(ref, on) {
  const [drag, setDrag] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !on) return undefined;
    let st = null, raf = 0, moved = false;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const snapBack = () => { el.style.scrollSnapType = ""; el.style.scrollBehavior = ""; };
    const down = (e) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      cancelAnimationFrame(raf);
      st = { x: e.clientX, left: el.scrollLeft, t: performance.now(), v: 0, lx: e.clientX };
      moved = false;
    };
    const move = (e) => {
      if (!st) return;
      const dx = e.clientX - st.x;
      if (!moved && Math.abs(dx) > 6) { moved = true; setDrag(true); el.style.scrollSnapType = "none"; el.style.scrollBehavior = "auto"; el.setPointerCapture?.(e.pointerId); }
      if (!moved) return;
      const now = performance.now();
      st.v = (st.lx - e.clientX) / Math.max(1, now - st.t);
      st.t = now; st.lx = e.clientX;
      el.scrollLeft = st.left - dx;
    };
    const up = () => {
      if (!st) return;
      const was = moved, v0 = st.v;
      st = null;
      setDrag(false);
      if (!was) return;
      let v = still ? 0 : v0 * 16, last = performance.now();
      const glide = (now) => {
        const dt = (now - last) / 16; last = now;
        el.scrollLeft += v * dt; v *= Math.pow(0.92, dt);
        if (Math.abs(v) > 0.4) raf = requestAnimationFrame(glide); else snapBack();
      };
      if (Math.abs(v) > 0.4) raf = requestAnimationFrame(glide); else snapBack();
      const eat = (ev) => { ev.preventDefault(); ev.stopPropagation(); };  // the click that ends a drag doesn't open a tile
      el.addEventListener("click", eat, { capture: true, once: true });
      setTimeout(() => el.removeEventListener("click", eat, { capture: true }), 0);
    };
    const nodrag = (e) => e.preventDefault();
    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
    el.addEventListener("dragstart", nodrag);
    return () => { cancelAnimationFrame(raf); ["pointerdown", "pointermove", "pointerup", "pointercancel"].forEach((n, i) => el.removeEventListener(n, [down, move, up, up][i])); el.removeEventListener("dragstart", nodrag); };
  }, [ref, on]);
  return drag;
}

function useEdges(ref, count) {
  const [s, setS] = useState({ over: false, start: true, end: true });
  const upd = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const over = el.scrollWidth - el.clientWidth > 2;
    setS({ over, start: el.scrollLeft <= 2, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2 });
  }, [ref]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    upd();
    el.addEventListener("scroll", upd, { passive: true });
    const ro = "ResizeObserver" in window ? new ResizeObserver(upd) : null;
    ro?.observe(el);
    return () => { el.removeEventListener("scroll", upd); ro?.disconnect(); };
  }, [ref, upd, count]);
  return s;
}

const ARROW = "absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-[var(--brand-primary)] shadow-md transition-[opacity,background-color] duration-200 hover:bg-slate-50 motion-reduce:transition-none";

export function HomeTiles({ tiles, preview = false, phone = false }) {
  const row = useRef(null);
  const w = phone ? WP : W;
  const { over, start, end } = useEdges(row, tiles.length);
  const drag = useDragScroll(row, over && !preview);
  const by = (d) => row.current?.scrollBy({ left: d * row.current.clientWidth * 0.8, behavior: smooth() });
  const onKey = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const n = row.current?.querySelector(`[data-i="${Number(e.currentTarget.dataset.i) + (e.key === "ArrowRight" ? 1 : -1)}"]`);
    if (!n) return;
    e.preventDefault();
    n.focus({ preventScroll: true });
    n.scrollIntoView({ inline: "nearest", block: "nearest", behavior: smooth() });
  };
  const id = preview ? (phone ? "preview-tiles-phone" : "preview-tiles") : "home-tiles";
  return (
    <div className="relative" data-testid={`${id}-wrap`} data-overflow={over ? "1" : "0"}>
      <div ref={row} role="list" aria-label="Product categories" data-testid={id} data-dragging={drag ? "1" : "0"}
        className={`rs-tiles-row ${over && !preview ? (drag ? "cursor-grabbing select-none" : "cursor-grab") : ""} -mx-5 flex gap-3 overflow-x-auto overscroll-x-contain scroll-px-5 px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${over ? "snap-x snap-mandatory" : ""} ${w.row} ${phone ? "" : "lg:mx-0 lg:scroll-px-0 lg:px-0"}`}>
        {tiles.map((t, i) => <div key={t.category_id} role="listitem" className="contents"><Tile t={t} i={i} preview={preview} w={w} onKey={onKey} /></div>)}
      </div>
      {over && !start && <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 -left-5 w-10 bg-gradient-to-r from-[#f7f8fb] to-transparent lg:left-0" data-testid={`${id}-fade-left`} />}
      {over && !end && <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 -right-5 w-10 bg-gradient-to-l from-[#f7f8fb] to-transparent lg:right-0" data-testid={`${id}-fade-right`} />}
      {over && !start && !phone && <button type="button" aria-label="Scroll categories left" onClick={() => by(-1)} className={`${ARROW} -left-4 lg:flex`} data-testid={`${id}-prev`}><ChevronLeft size={20} aria-hidden="true" /></button>}
      {over && !end && !phone && <button type="button" aria-label="Scroll categories right" onClick={() => by(1)} className={`${ARROW} -right-4 lg:flex`} data-testid={`${id}-next`}><ChevronRight size={20} aria-hidden="true" /></button>}
    </div>
  );
}

// Reserves the exact row height while tiles load (no CLS).
export function TilesSkeleton({ className = "", pulse = true }) {
  return (
    <div className={`-mx-5 flex gap-3 overflow-hidden px-5 pb-1 lg:mx-0 lg:px-0 ${W.row} ${className}`} aria-hidden="true" data-testid="home-tiles-skeleton">
      {Array.from({ length: 8 }).map((_, i) => <div key={i} className={`shrink-0 rounded-2xl bg-[#E8F0FA] ${W.tile} ${pulse ? "animate-pulse motion-reduce:animate-none" : ""}`} />)}
    </div>
  );
}
